import type { VNode } from 'vue'
import type { Toast, ToastGroup } from '@nordhealth/components'
import { useSafeId } from '@/composables/useSafeId.js'

export type AddToast = (content: string | VNode, options?: ToastOptions) => void
type ToastOptions = Parameters<ToastGroup['addToast']>[1]
type ToastData = {
  id: string
  content: () => VNode
  variant: Toast['variant']
  autoDismiss: Toast['autoDismiss']
}

// -- CONSTANTS --
export const COMMON_AUTO_DISMISS_DURATION = 3000

// -- STATE --
const toasts = ref<ToastData[]>([])
let toastCounter = 0

// -- INTERNAL FUNCTIONS --
const normalizeToastContent = (content: string | VNode) => {
  if (typeof content === 'string') {
    return () => h('span', content)
  }

  return () => content
}

const addToast: AddToast = (content, options) => {
  // Use useSafeId() when in Vue context, fall back to `toastCounter` when called
  // from async callbacks outside component context (e.g., windowListen)
  const id = getCurrentInstance() ? useSafeId() : `toast-${++toastCounter}`
  toasts.value.push({
    id,
    content: normalizeToastContent(content),
    variant: options?.variant ?? 'default',
    autoDismiss: options?.autoDismiss ?? COMMON_AUTO_DISMISS_DURATION,
  })
}

const removeToast = (id: string) => {
  toasts.value = toasts.value.filter((t) => t.id !== id)
}

// -- COMPOSABLES --
// Creates the provide/inject pair for toast state management.
// useProvideToast() must be called in a parent component to provide the context.
// useToast() can then be called in any child component to access the toast state.
const [useProvideToast, useToast] = createInjectionState(() => {
  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
  }
})

// Returns only the provided addToast function.
// This is the main API for components that just need to add toasts.
const useAddToast = () => {
  const injected = useToast()
  if (!injected) {
    throw new Error('No provider found for useAddToast')
  }
  return injected.addToast
}

export { useProvideToast, useAddToast }
